import { useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SLIDE_IMAGES = [
    "https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/x6i6noyd_med1.png",
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_medx-dashboard-demo/artifacts/pt7swave_MEDX-AI-Welcome-Poster.pptx.png",
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_medx-dashboard-demo/artifacts/offs3orj_WhatsApp%20Image%202026-07-18%20at%203.26.03%20PM.jpeg",
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_medx-dashboard-demo/artifacts/dopjo1vk_WhatsApp%20Image%202026-07-23%20at%204.54.30%20PM.jpeg",
];
const SLIDE_LINKS = ["/solutions", "/solutions", "/order", "/solutions#vision"];

export function HeroSlider() {
    const { t } = useTranslation();
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        const tt = setInterval(() => setIdx((i) => (i + 1) % SLIDE_IMAGES.length), 6500);
        return () => clearInterval(tt);
    }, []);

    const slides = [
        { eyebrow: t("hero.slide1.eyebrow"), title: t("hero.slide1.title"), description: t("hero.slide1.description"), cta: t("hero.slide1.cta") },
        { eyebrow: t("hero.slide2.eyebrow"), title: t("hero.slide2.title"), description: t("hero.slide2.description"), cta: t("hero.slide2.cta") },
        { eyebrow: t("hero.slide3.eyebrow"), title: t("hero.slide3.title"), description: t("hero.slide3.description"), cta: t("hero.slide3.cta") },
        { eyebrow: t("hero.slide4.eyebrow"), title: t("hero.slide4.title"), description: t("hero.slide4.description"), cta: t("hero.slide4.cta") },
    ];

    const slide = slides[idx];

    return (
        <section
            className="relative w-full h-[88vh] min-h-[640px] overflow-hidden bg-black"
            data-testid="hero-slider"
        >
            {SLIDE_IMAGES.map((img, i) => (
                <div
                    key={img}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                        i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                >
                    <img
                        src={img}
                        alt={slides[i].title}
                        className={`w-full h-full object-cover ${i === idx ? "kenburns" : ""}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/15" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/30" />
                </div>
            ))}

            <div className="relative z-10 max-w-7xl mx-auto h-full px-6 md:px-12 flex items-center">
                <div className="max-w-2xl text-white pt-20">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--medx-red)]/20 border border-[var(--medx-red)]/40 backdrop-blur text-xs font-mono tracking-[0.18em] text-orange-100 mb-6"
                        data-testid={`hero-slide-${idx}-eyebrow`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--medx-orange)] animate-pulse" />
                        {slide.eyebrow}
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tighter mb-6 text-white"
                        data-testid={`hero-slide-${idx}-title`}>
                        {slide.title}
                    </h1>
                    <p className="text-lg md:text-xl text-gray-200/90 max-w-xl mb-8 leading-relaxed">
                        {slide.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-4">
                        <Link to={SLIDE_LINKS[idx]} className="btn-primary inline-flex items-center gap-2" data-testid={`hero-slide-${idx}-cta`}>
                            {slide.cta} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-8 left-0 right-0 z-10 max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {SLIDE_IMAGES.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setIdx(i)}
                            className={`h-1.5 rounded-full transition-all ${
                                i === idx ? "w-12 bg-[var(--medx-orange)]" : "w-6 bg-white/40 hover:bg-white/70"
                            }`}
                            data-testid={`hero-dot-${i}`}
                            aria-label={`Slide ${i + 1}`}
                        />
                    ))}
                </div>
                <div className="hidden md:flex items-center gap-2">
                    <button
                        onClick={() => setIdx((i) => (i - 1 + SLIDE_IMAGES.length) % SLIDE_IMAGES.length)}
                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 grid place-items-center text-white"
                        data-testid="hero-prev"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setIdx((i) => (i + 1) % SLIDE_IMAGES.length)}
                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 grid place-items-center text-white"
                        data-testid="hero-next"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="hidden lg:block absolute bottom-20 right-12 z-10 bg-white/95 backdrop-blur rounded-2xl p-5 shadow-2xl border border-white/40 w-72">
                <div className="text-xs font-mono text-gray-500 tracking-widest mb-2">{t("hero.liveFromKiosk")}</div>
                <div className="flex items-end justify-between">
                    <div>
                        <div className="text-3xl font-display font-bold text-gray-900">98<span className="text-[var(--medx-red)]">%</span></div>
                        <div className="text-xs text-gray-500">{t("hero.spo2Accuracy")}</div>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white font-bold">
                        AI
                    </div>
                </div>
            </div>
        </section>
    );
}
